import type { ReactElement } from 'react'
import { useState } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { UnitOverviewEntry } from '@/api/analytics'
import type { DatedProvenance, Provenance } from '@/api/networth'
import type { UnitSeries } from '@/domain/units'
import { formatMinor } from '~/api/client'
import { ChartCard } from '~/charts/ChartCard'
import { formatAxisDate } from '~/charts/format'
import { chartCss, seriesColor } from '~/charts/theme'
import { UnitDialog } from '~/components/UnitDialog'
import { Card, CardHead, StatCard } from '~/components/ui/Card'
import { Button, Filter } from '~/components/ui/Controls'
import { StatusPill } from '~/components/ui/Status'
import { DataTable } from '~/components/ui/Table'
import { daysBetween } from '~/state/dates'
import type { Measure } from '~/state/queries'
import {
  useEffectiveValuations,
  useHouseholdShape,
  usePrivateHoldings,
  useUnitSeries,
  useUnitsOverview,
} from '~/state/queries'
import type { UnitChartPoint } from '~/state/units'
import {
  firstChartableKey,
  formatUnitCount,
  sortSeries,
  thinHistory,
  toChartPoints,
  unitChange,
} from '~/state/units'
import { formatSignedBasisPoints, includesPrivateHoldings } from '~/state/private-holdings'
import { figuresFor, shownFigure, spendableAssumption } from '~/state/figures'

const RANGES: { days: number; label: string }[] = [
  { days: 90, label: '3 months' },
  { days: 365, label: '1 year' },
  { days: 1825, label: '5 years' },
]

const PAGE_CSS = `
.nw-units .nw-stats { display: flex; flex-wrap: wrap; gap: var(--nw-space-6); margin-bottom: var(--nw-stack); }
.nw-units .nw-stats > * { flex: 1 1 220px; }
.nw-units .nw-marks { display: inline-flex; gap: var(--nw-space-1); margin-left: var(--nw-space-2); }
.nw-units small { color: var(--nw-muted); }
/* The invitation under the empty state, and the one beside the table once
   there is a table. Centred where it is the only thing on the page. */
.nw-units .nw-units-act { display: flex; gap: var(--nw-space-2); justify-content: center; }
/* Five columns, one of them a button, do not fit a phone. The table keeps its
   width and the card scrolls it, exactly as the warrants table already does. */
.nw-units .nw-table-wrap .nw-data { min-width: 900px; }
/* How thin a unit's history is, under the date it was last priced rather than
   running on from it. */
.nw-units .nw-history { display: block; }
`

const UNIT_COLUMNS = [
  { id: 'unit', label: 'Unit', width: '260px' },
  { id: 'price', label: 'Price', width: '150px', align: 'right' as const },
  { id: 'priced', label: 'Priced', width: '230px' },
  { id: 'buys', label: 'Buys', width: '140px', align: 'right' as const },
  { id: 'act', label: '', width: '120px' },
]

const POINT_COLUMNS = [
  { id: 'date', label: 'Date', width: '130px' },
  { id: 'units', label: 'Units', width: '150px', align: 'right' as const },
  { id: 'worth', label: 'Net worth', width: '170px', align: 'right' as const },
  { id: 'price', label: 'Unit price', width: '150px', align: 'right' as const },
  { id: 'data', label: 'Data' },
]

/** Closed, defining the first one, or pricing the one named. */
type Dialog = { unit: UnitOverviewEntry | null } | null

export function Units(): ReactElement {
  const { settings } = usePrivateHoldings()
  const valuations = useEffectiveValuations()
  const holdsPrivately = (valuations.data?.valuations.length ?? 0) > 0
  const [measure, setMeasure] = useState<Measure>('lookThrough')
  const figures = figuresFor(useHouseholdShape(settings) ?? { company: false, unsellable: false })
  const shown = shownFigure(figures, measure)
  const assumption = spendableAssumption(shown)
  const [days, setDays] = useState(365)
  const [chosenKey, setChosenKey] = useState<string | null>(null)
  const [view, setView] = useState<'chart' | 'table'>('chart')
  const [dialog, setDialog] = useState<Dialog>(null)

  const overview = useUnitsOverview(settings, shown)
  const series = useUnitSeries(settings, shown, days)

  const all = sortSeries(series.data?.series ?? [])
  const key = chosenKey ?? firstChartableKey(all)
  const chosen = all.find((entry) => entry.key === key) ?? null
  const error = overview.error ?? series.error

  const defined = overview.data?.units ?? []
  const baseCurrency = overview.data?.baseCurrency ?? ''
  const opened =
    dialog === null ? null : (
      <UnitDialog
        {...(dialog.unit === null ? {} : { unit: dialog.unit })}
        baseCurrency={baseCurrency}
        onClose={() => setDialog(null)}
      />
    )

  // One empty state rather than two. The page used to say "0 units" in its
  // header, draw a table with no rows, and separately report that no unit had a
  // price covering the range — three statements of the same absence, none of
  // which said that a household may define one.
  if (overview.data !== undefined && defined.length === 0) {
    return (
      <main className="nw nw-units">
        <style>{chartCss + PAGE_CSS}</style>
        <h1>Units</h1>
        <p className="nw-note">
          A number that grows while the thing it buys grows faster has not grown. This page divides
          your net worth by whatever you would rather read it in — an espresso, a square metre of a
          flat, a gram of gold — and separates the part the wealth moved from the part the price
          did.
        </p>
        <p className="nw-empty">
          No unit is defined yet, and none is shipped with the product: a yardstick you did not
          choose is not a yardstick.
        </p>
        <div className="nw-units-act">
          <Button kind="primary" onClick={() => setDialog({ unit: null })}>
            Define a unit
          </Button>
        </div>
        {opened}
      </main>
    )
  }

  const dayFlags = series.data?.provenance ?? []
  const charted = {
    projected: dayFlags.filter((day) => day.projected).length,
    stale: dayFlags.filter((day) => day.stale).length,
    total: dayFlags.length,
  }
  const byDate = new Map(dayFlags.map((day) => [day.date, day]))
  const latest: Provenance | null = overview.data ?? null

  return (
    <main className="nw nw-units">
      <style>{chartCss + PAGE_CSS}</style>
      <h1>Units</h1>
      {overview.data && (
        <p className="nw-sub">
          {`${formatMinor(overview.data.amountMinor, overview.data.baseCurrency)} on ${overview.data.date} · ${defined.length} ${defined.length === 1 ? 'unit' : 'units'}`}
          {(latest?.projected || latest?.stale) && (
            <span className="nw-marks">
              {latest.projected && <StatusPill tone="caution" label="Projected" />}
              {latest.stale && (
                <StatusPill
                  tone="failed"
                  label={`Stale${latest.staleSince ? ` since ${latest.staleSince}` : ''}`}
                />
              )}
            </span>
          )}
        </p>
      )}
      <p className="nw-note">
        A number that grows while the thing it buys grows faster has not grown. Each unit carries
        its own price history, so the count moves for two reasons and both are shown separately.
      </p>

      <div className="nw-filters">
        <Filter
          label="Figure"
          choices={figures.map((entry) => ({ id: entry.key, label: entry.label }))}
          value={shown}
          onChange={(id) => setMeasure(id as Measure)}
        />
        <Filter
          label="Range"
          choices={RANGES.map((range) => ({ id: String(range.days), label: range.label }))}
          value={String(days)}
          onChange={(id) => setDays(Number(id))}
        />
        {all.length > 0 && (
          <Filter
            label="Unit"
            choices={all.map((entry) => ({ id: entry.key, label: entry.label }))}
            value={key ?? ''}
            onChange={setChosenKey}
          />
        )}
      </div>

      {assumption !== null && <p className="nw-note">{assumption}</p>}

      {/* The figure on screen rather than the one chosen: the two differ only
          where the chosen one is no longer offered, and this is the sentence
          whose job is to stop the counts above it being misread.

          Both spellings of it presume a private holding exists — one says the
          counts rest on a round, the other that something is being left out —
          so a household with none is told either that its figures rest on a
          valuation it does not have or that it is missing one it never made.
          The switch it describes moves nothing for them: including none of
          something adds nothing, so there is no misreading left to stop. */}
      {holdsPrivately && (
        <p className="nw-note">
          {includesPrivateHoldings(settings, shown)
            ? 'Private holdings are counted in this figure, so these counts rest partly on a private round.'
            : 'Private holdings are left out of this figure.'}
        </p>
      )}

      {error && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>Could not load units: {error.message}</span>
        </p>
      )}

      {(latest?.projected || charted.projected > 0) && (
        <p className="nw-flag nw-flag--modelled">
          <span className="nw-flag-icon" aria-hidden="true">
            ◆
          </span>
          <span>
            <b>Modelled</b> —{' '}
            {latest?.projected &&
              `the count on ${overview.data?.date} rests partly on positions no source confirmed.`}{' '}
            {charted.projected > 0 &&
              `${charted.projected} of the ${charted.total} days charted rest on unconfirmed positions.`}
          </span>
        </p>
      )}

      {(latest?.stale || charted.stale > 0) && (
        <p className="nw-flag nw-flag--stale">
          <span className="nw-flag-icon" aria-hidden="true">
            ▲
          </span>
          <span>
            <b>Carried forward</b> —{' '}
            {latest?.stale &&
              `nothing has reported since ${latest.staleSince ?? 'an unknown date'}; the last known positions are repeated rather than counted as a loss.`}{' '}
            {charted.stale > 0 &&
              `${charted.stale} of the ${charted.total} days charted repeat an earlier day's positions.`}
          </span>
        </p>
      )}

      {chosen && (
        <Chart
          series={chosen}
          definition={defined.find((entry) => entry.key === chosen.key) ?? null}
          provenance={byDate}
          view={view}
          onViewChange={setView}
        />
      )}

      {series.data && all.length > 0 && all.every((entry) => entry.points.length === 0) && (
        <p className="nw-empty">
          No unit has a price covering this range, so there is nothing to divide the net worth by.
        </p>
      )}

      {overview.data && (
        <Card>
          <CardHead
            title="Every unit today"
            aside={
              <Button kind="primary" onClick={() => setDialog({ unit: null })}>
                Define a unit
              </Button>
            }
          />
          <div className="nw-table-wrap">
            <DataTable columns={UNIT_COLUMNS} label="Every unit, priced today">
              {overview.data.units.map((unit) => (
                <OverviewRow key={unit.key} unit={unit} onPrice={() => setDialog({ unit })} />
              ))}
            </DataTable>
          </div>
        </Card>
      )}
      {opened}
    </main>
  )
}

function OverviewRow({
  unit,
  onPrice,
}: {
  unit: UnitOverviewEntry
  onPrice: () => void
}): ReactElement {
  return (
    <tr>
      <th scope="row">{unit.label}</th>
      <td className="nw-num">
        {unit.priceMinor === null ? (
          <small>not priced</small>
        ) : (
          formatMinor(unit.priceMinor, unit.currency)
        )}
      </td>
      <td>
        {unit.priceDate ?? '—'} {unit.priceSource && <small>({unit.priceSource})</small>}
        <small className="nw-history">
          {unit.observations === 1
            ? 'one price only'
            : `${unit.observations} prices · ${unit.firstDate} to ${unit.lastDate}`}
        </small>
      </td>
      <td className="nw-num">{unit.unitsHeld === null ? '—' : formatUnitCount(unit.unitsHeld)}</td>
      <td>
        <Button onClick={onPrice}>Prices</Button>
      </td>
    </tr>
  )
}

function Chart({
  series,
  definition,
  provenance,
  view,
  onViewChange,
}: {
  series: UnitSeries
  /** What the household has on file for this unit, which is where thin history shows. */
  definition: UnitOverviewEntry | null
  provenance: ReadonlyMap<string, DatedProvenance>
  view: 'chart' | 'table'
  onViewChange: (view: 'chart' | 'table') => void
}): ReactElement {
  const points = toChartPoints(series)
  const change = unitChange(series.summary)
  const last = points[points.length - 1]
  const thin = definition === null ? null : thinHistory(definition)

  return (
    <>
      {change && series.summary && (
        <div className="nw-stats">
          <StatCard
            label="Units"
            note={`${series.summary.firstDate} to ${series.summary.lastDate}`}
          >
            {formatSignedBasisPoints(change.units)}
          </StatCard>
          <StatCard label="Of which net worth" note="The part you did.">
            {change.amount === null ? '—' : formatSignedBasisPoints(change.amount)}
          </StatCard>
          <StatCard label="Of which unit price" note="The part the world did.">
            {change.unitPrice === null ? '—' : formatSignedBasisPoints(change.unitPrice)}
          </StatCard>
        </div>
      )}

      <ChartCard
        title={`${series.label} over time`}
        description={
          last
            ? `How many the net worth buys. One ${series.label} cost ${formatMinor(last.unitPriceBaseMinor, series.baseCurrency)} on ${last.date}.`
            : 'No price history reaches this range.'
        }
        view={view}
        onViewChange={onViewChange}
        legend={[{ label: series.label, color: seriesColor(0), shape: 'line' }]}
      >
        {view === 'chart' ? (
          <div className="nw-plot">
            <UnitLine points={points} label={series.label} currency={series.baseCurrency} />
          </div>
        ) : (
          <PointsTable
            points={points}
            currency={series.baseCurrency}
            provenance={provenance}
          />
        )}
      </ChartCard>

      {thin !== null && (
        <p className="nw-flag nw-flag--modelled">
          <span className="nw-flag-icon" aria-hidden="true">
            ◆
          </span>
          <span>
            <b>One price</b> — {thin} Add what one cost a year ago, and five, to make the comparison
            this page is for.
          </span>
        </p>
      )}

      {series.unpricedDates.length > 0 && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>
            {series.unpricedDates.length} day
            {series.unpricedDates.length === 1 ? '' : 's'} in this range are missing from the line
            because the unit had no price yet — the first is {series.unpricedDates[0]}. A price
            nobody published is not invented backwards.
          </span>
        </p>
      )}
    </>
  )
}

function UnitLine({
  points,
  label,
  currency,
}: {
  points: readonly UnitChartPoint[]
  label: string
  currency: string
}): ReactElement {
  const first = points[0]
  const last = points[points.length - 1]
  const spanDays = first && last ? daysBetween(first.date, last.date) : 0

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={points as UnitChartPoint[]} margin={{ top: 8, right: 34, bottom: 0, left: 8 }}>
        <CartesianGrid stroke="var(--nw-grid)" strokeWidth={1} vertical={false} />
        <XAxis
          dataKey="date"
          axisLine={{ stroke: 'var(--nw-axis)' }}
          tickLine={false}
          minTickGap={48}
          tick={{ fill: 'var(--nw-muted)', fontSize: 12 }}
          tickFormatter={(date: string) => formatAxisDate(date, spanDays)}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          width={72}
          tick={{ fill: 'var(--nw-muted)', fontSize: 12 }}
          tickFormatter={(value: number) => formatUnitCount(String(value))}
        />
        <Tooltip
          cursor={{ stroke: 'var(--nw-axis)', strokeWidth: 1 }}
          content={({ active, payload }) =>
            active ? (
              <UnitTooltip
                point={payload?.[0]?.payload as UnitChartPoint | undefined}
                label={label}
                currency={currency}
              />
            ) : null
          }
        />
        <Line
          type="monotone"
          dataKey="units"
          name={label}
          stroke={seriesColor(0)}
          strokeWidth={2}
          strokeLinecap="round"
          dot={false}
          activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--nw-surface)' }}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

function UnitTooltip({
  point,
  label,
  currency,
}: {
  point: UnitChartPoint | undefined
  label: string
  currency: string
}): ReactElement | null {
  if (!point) return null

  return (
    <div className="nw-tooltip">
      <h3>{point.date}</h3>
      <dl>
        <div>
          <dt />
          <dd className="nw-tooltip-name">{label}</dd>
          <dd className="nw-tooltip-value">{formatUnitCount(point.unitsText)}</dd>
        </div>
        <div>
          <dt />
          <dd className="nw-tooltip-name">Net worth</dd>
          <dd className="nw-tooltip-value">{formatMinor(point.amountMinor, currency)}</dd>
        </div>
        <div>
          <dt />
          <dd className="nw-tooltip-name">Unit price</dd>
          <dd className="nw-tooltip-value">{formatMinor(point.unitPriceBaseMinor, currency)}</dd>
        </div>
      </dl>
    </div>
  )
}

function PointsTable({
  points,
  currency,
  provenance,
}: {
  points: readonly UnitChartPoint[]
  currency: string
  provenance: ReadonlyMap<string, DatedProvenance>
}): ReactElement {
  return (
    <div className="nw-table-wrap">
      <DataTable dense columns={POINT_COLUMNS} label={`Units by day, in ${currency}`}>
        {points.map((point) => {
          const day = provenance.get(point.date)
          const flagged = day !== undefined && (day.projected || day.stale)
          return (
            <tr key={point.date} data-flagged={flagged}>
              <th scope="row">{point.date}</th>
              <td className="nw-num">{formatUnitCount(point.unitsText)}</td>
              <td className="nw-num">{formatMinor(point.amountMinor, currency)}</td>
              <td className="nw-num">{formatMinor(point.unitPriceBaseMinor, currency)}</td>
              <td>
                {day?.projected && 'Modelled'}
                {day?.projected && day.stale && ' · '}
                {day?.stale && 'Carried forward'}
                {!flagged && 'Fetched'}
              </td>
            </tr>
          )
        })}
      </DataTable>
    </div>
  )
}
